import { useState, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import {
  ArrowRightLeft,
  KeyRound,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Clock,
  Shield,
  ExternalLink,
} from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { HeirShell } from '@/components/HeirShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SignerBadge, WindowCountdown } from '@/components/nip41/KeyChainDiagram';
import {
  NIP41_KIND_WHITELIST,
  NIP41_KIND_MIGRATION,
  NIP41_KIND_TIMESTAMP_PROOF,
  NIP41_SPEC_WARNING,
  NIP41_CONTESTATION_WINDOW_MS,
  LS_FIRST_SEEN_PREFIX,
  buildMigrationTags,
  getWhitelistSuccessor,
  isValidWhitelistEvent,
} from '@/lib/nip41';
import { createTimestampProof, buildTimestampEvent } from '@/lib/otsService';
import { cn } from '@/lib/utils';

type MigrateStep = 'connect' | 'find' | 'review' | 'publish' | 'done';

export default function MigratePage() {
  useSeoMeta({
    title: 'Migrate — Heir (NIP-41)',
    description: 'Execute break-glass migration to your successor key.',
  });

  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutateAsync: publishEvent } = useNostrPublish();

  const [step, setStep] = useState<MigrateStep>('connect');
  const [oldPubkey, setOldPubkey] = useState('');
  const [ephemeralNsec, setEphemeralNsec] = useState('');
  const [useEphemeral, setUseEphemeral] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedEvents, setPublishedEvents] = useState<any[]>([]);
  const [firstSeenAt, setFirstSeenAt] = useState<number | null>(null);
  const [error, setError] = useState('');

  // Find whitelist data when we have the old pubkey
  const { data: whitelistData, isLoading: findingWhitelist } = useQuery({
    queryKey: ['migrate-find-whitelist', oldPubkey],
    queryFn: async () => {
      if (!oldPubkey) return null;

      // Query whitelist events by the old key
      const whitelists = await nostr.query([{
        kinds: [NIP41_KIND_WHITELIST],
        authors: [oldPubkey],
      }]);

      if (whitelists.length === 0) return null;

      const whitelist = whitelists[0];
      if (!isValidWhitelistEvent(whitelist)) return null;

      // Find the timestamp proof for this whitelist
      const proofs = await nostr.query([{
        kinds: [NIP41_KIND_TIMESTAMP_PROOF],
        '#e': [whitelist.id],
      }]);

      const proof = proofs[0];

      // Verify the successor pubkey matches current user
      const successorPubkey = getWhitelistSuccessor(whitelist);
      const currentPubkey = useEphemeral
        ? getPubkeyFromNsec(ephemeralNsec)
        : user?.pubkey;

      return {
        whitelist,
        proof,
        successorPubkey,
        matches: successorPubkey === currentPubkey,
      };
    },
    enabled: step === 'find' && !!oldPubkey && (!!user || useEphemeral),
  });

  useEffect(() => {
    if (whitelistData && step === 'find') {
      if (whitelistData.matches) {
        setStep('review');
      } else {
        setError('The connected key does not match the successor in the whitelist.');
      }
    }
  }, [whitelistData, step]);

  const handleFindWhitelist = () => {
    if (!oldPubkey) {
      setError('Please enter the old pubkey');
      return;
    }
    setError('');
    setStep('find');
  };

  const handlePublishMigration = async () => {
    if (!whitelistData) return;
    setIsPublishing(true);
    setError('');

    try {
      const { whitelist, proof } = whitelistData;

      // Build migration tags
      const migrationTags = buildMigrationTags(
        oldPubkey,
        whitelist.id,
        proof?.id || '',
        ['wss://relay.ditto.pub', 'wss://relay.primal.net', 'wss://relay.damus.io']
      );

      // Publish migration event (kind 1777)
      const migrationEvent = await publishEvent({
        kind: NIP41_KIND_MIGRATION,
        content: '',
        tags: migrationTags,
      });

      // Create timestamp proof for the migration
      const pendingProof = await createTimestampProof(
        migrationEvent.id,
        migrationEvent.id,
        NIP41_KIND_MIGRATION
      );

      // Publish timestamp proof
      const timestampTemplate = buildTimestampEvent(pendingProof, migrationEvent.pubkey);
      const timestampEvent = await publishEvent({
        kind: NIP41_KIND_TIMESTAMP_PROOF,
        content: timestampTemplate.content,
        tags: timestampTemplate.tags,
      });

      // Record first seen
      const firstSeen = Date.now();
      localStorage.setItem(LS_FIRST_SEEN_PREFIX + migrationEvent.id, firstSeen.toString());
      setFirstSeenAt(firstSeen);

      setPublishedEvents([migrationEvent, timestampEvent]);
      setStep('done');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <HeirShell>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Migrate</h1>
          <p className="text-slate-400">
            Break-glass: declare migration from a compromised key to your pre-positioned successor.
          </p>
        </div>

        {/* Warning Banner */}
        <div className="mb-6 bg-red-950/20 border border-red-500/30 rounded-xl p-4 flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-400 mb-1">High-stress operation</h3>
            <p className="text-xs text-red-300/80">
              Migration is rare and executed under duress. Your followers will not auto-update 
              until the contestation window closes. Post from the new key and tell people out of band.
            </p>
          </div>
        </div>

        {/* Draft Warning */}
        <div className="mb-6 bg-amber-950/20 border border-amber-500/20 rounded-lg p-3 flex items-start space-x-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300">{NIP41_SPEC_WARNING}</p>
        </div>

        {/* Stepper */}
        <StepIndicator currentStep={step} />

        {/* Step Content */}
        <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6">
          {step === 'connect' && (
            <div className="space-y-6">
              <div className="text-center">
                <ArrowRightLeft className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Connect Successor Key</h2>
                <p className="text-slate-400 text-sm">
                  Connect the <strong>successor</strong> key — the one you armed earlier.
                </p>
              </div>

              {user ? (
                <div className="space-y-4">
                  <SignerBadge pubkey={user.pubkey} label="Connected Successor Key" color="amber" />
                  <Button
                    onClick={() => setStep('find')}
                    className="w-full bg-amber-600 hover:bg-amber-500 text-white"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <p className="text-sm text-slate-500">
                    Please log in with your successor key via NIP-07 extension.
                  </p>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-800" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-slate-950 text-slate-600">or</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs text-slate-600">
                      If the successor key is not in an extension, you can paste the nsec once.
                      It is held in memory only and never persisted.
                    </p>
                    <div className="flex space-x-2">
                      <Input
                        type="password"
                        value={ephemeralNsec}
                        onChange={(e) => setEphemeralNsec(e.target.value)}
                        placeholder="nsec1..."
                        className="flex-1 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
                      />
                      <Button
                        onClick={() => {
                          setUseEphemeral(true);
                          setStep('find');
                        }}
                        disabled={!ephemeralNsec.startsWith('nsec1')}
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-950/30"
                      >
                        Use Once
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'find' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Find Whitelist</h2>
                <p className="text-slate-400 text-sm">
                  Enter the pubkey (or npub) of the <strong>old</strong> key that whitelisting you.
                </p>
              </div>

              <div className="flex space-x-3">
                <Input
                  value={oldPubkey}
                  onChange={(e) => setOldPubkey(e.target.value)}
                  placeholder="npub1... or hex pubkey"
                  className="flex-1 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
                  onKeyDown={(e) => e.key === 'Enter' && handleFindWhitelist()}
                />
                <Button
                  onClick={handleFindWhitelist}
                  disabled={!oldPubkey}
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                >
                  {findingWhitelist ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {error && (
                <p className="text-sm text-red-400">{error}</p>
              )}

              <p className="text-xs text-slate-600">
                Heir will search relays for a kind:1776 whitelist event from this key 
                that points to your connected successor key.
              </p>
            </div>
          )}

          {step === 'review' && whitelistData && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Review Migration</h2>
                <p className="text-slate-400 text-sm">
                  Review the migration event before signing.
                </p>
              </div>

              {user && (
                <SignerBadge pubkey={user.pubkey} label="Will be signed by (successor)" color="amber" />
              )}

              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Event Type</span>
                  <span className="text-xs font-mono text-amber-400">kind:1777 Migration</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Old Key</span>
                  <span className="text-xs font-mono text-slate-300">{oldPubkey.slice(0, 20)}...</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Successor</span>
                  <span className="text-xs font-mono text-cyan-400">
                    {whitelistData.successorPubkey?.slice(0, 20)}...
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Whitelist Ref</span>
                  <span className="text-xs font-mono text-slate-400">{whitelistData.whitelist.id.slice(0, 16)}...</span>
                </div>
              </div>

              <div className="bg-amber-950/20 border border-amber-500/20 rounded-lg p-4">
                <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>What happens after publishing</span>
                </h4>
                <ul className="space-y-2 text-sm text-amber-300/80">
                  <li>A 60-day contestation window begins</li>
                  <li>Followers on NIP-41-aware clients stay pointed at the old key until the window closes</li>
                  <li>If you detect a competing migration, publish another from this same key</li>
                  <li>After the window closes, clients may update follow lists</li>
                </ul>
              </div>

              <Button
                onClick={handlePublishMigration}
                disabled={isPublishing}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Sign & Publish Migration
                  </>
                )}
              </Button>

              {error && (
                <p className="text-sm text-red-400 text-center">{error}</p>
              )}
            </div>
          )}

          {step === 'done' && (
            <div className="space-y-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto" />
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Migration Published</h2>
                <p className="text-slate-400 text-sm">
                  Your migration declaration is now on the network. The contestation window has begun.
                </p>
              </div>

              {firstSeenAt && (
                <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-left">
                  <WindowCountdown firstSeenAt={firstSeenAt} />
                </div>
              )}

              {publishedEvents.length > 0 && (
                <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-left space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Migration Event</span>
                    <code className="text-xs font-mono text-amber-400">{publishedEvents[0].id.slice(0, 16)}...</code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Timestamp Proof</span>
                    <code className="text-xs font-mono text-amber-400">{publishedEvents[1]?.id.slice(0, 16)}...</code>
                  </div>
                </div>
              )}

              <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-left">
                <h4 className="text-sm font-medium text-slate-300 mb-3">What to do now</h4>
                <ol className="space-y-3 text-sm text-slate-400 list-decimal list-inside">
                  <li>
                    <strong className="text-slate-300">Post from the new key</strong> and tell your followers 
                    out of band (other social media, email, etc.) that you have migrated.
                  </li>
                  <li>
                    <strong className="text-slate-300">Arm a new successor</strong> from this new key immediately. 
                    The chain only stays protected if the next link is always pre-positioned.
                  </li>
                  <li>
                    <strong className="text-slate-300">Monitor for contests</strong> — inspect your old npub 
                    periodically to ensure no competing migration appears.
                  </li>
                </ol>
              </div>

              <a
                href="/arm"
                className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 text-sm"
              >
                <Shield className="w-4 h-4" />
                <span>Arm a new successor now</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      </div>
    </HeirShell>
  );
}

function StepIndicator({ currentStep }: { currentStep: MigrateStep }) {
  const steps: { id: MigrateStep; label: string }[] = [
    { id: 'connect', label: 'Connect' },
    { id: 'find', label: 'Find' },
    { id: 'review', label: 'Review' },
    { id: 'publish', label: 'Publish' },
    { id: 'done', label: 'Done' },
  ];

  const currentIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, i) => (
        <div key={step.id} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-all',
                i <= currentIndex
                  ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                  : 'border-slate-700 bg-slate-900 text-slate-600'
              )}
            >
              {i < currentIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span className={cn('text-[10px] mt-1.5 uppercase tracking-wider', i <= currentIndex ? 'text-amber-400' : 'text-slate-600')}>
              {step.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={cn('w-8 sm:w-12 h-0.5 mx-1 sm:mx-2', i < currentIndex ? 'bg-amber-500/50' : 'bg-slate-800')} />
          )}
        </div>
      ))}
    </div>
  );
}

function getPubkeyFromNsec(nsec: string): string | undefined {
  try {
    const decoded = nip19.decode(nsec);
    if (decoded.type === 'nsec') {
      // We shouldn't be handling raw private keys in browser, but for the ephemeral fallback:
      // This is a simplified check - in reality we'd need to derive pubkey from the secret
      return undefined;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

// Simple icons
function ArrowRight(props: any) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function Search(props: any) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
    </svg>
  );
}
